import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import {
  isInternational,
  SchoolPlanTrialModal,
} from '../../components/_molecules/BlockedModals';
import { SchoolPlanTrialState } from '../../components/_molecules/BlockedModals/SchoolPlanTrialModal';
import { Slide } from '../../components/SlideComponents/slide.types';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import useAttioEventTracking from '../../hooks/useAttioEventTracking';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useMetrics } from '../../hooks/useMetrics';
import { usePaywallEmailTracking } from '../../hooks/usePaywallEmailTracking';
import { computeVisibleTrialsLeft } from '../../hooks/useSchoolPlanContentAccess';
import useTeachingCreditState from '../../hooks/useTeachingCreditState';
import { isPremiumTestPrepPresentation } from '../../utils/premiumContentDetection';

// Used when the backend hasn't returned `premiumTestPrepLimit` yet. Same
// value as VISIBLE_TRIAL_LIMIT today, kept distinct so a future backend bump
// doesn't accidentally lift the displayed cap.
const FALLBACK_LIMIT = 3;

interface PremiumTestPrepPlaywallContextType {
  /**
   * Call before starting (playing) a lesson. If the lesson is a School Plan
   * lesson AND the user needs to be gated, this opens the new modal and
   * returns `true` (caller should NOT continue to lesson start). Otherwise
   * returns `false` — caller proceeds as normal.
   *
   * When the first-run state confirms or the email-gate captures an email,
   * the provider invokes `onConfirm` itself, which replaces the original
   * "present lesson" action.
   *
   * `skipFirstRun` lets a caller start the lesson without the first-run
   * "this will use one of your School Plan Trials" screen — the trial is still
   * spent, and the later screens (email gate, hard block) still apply.
   */
  checkAndPromptPremiumTestPrepPaywall: (
    slides: Slide[],
    source: string,
    onConfirm: () => void,
    options?: { skipFirstRun?: boolean },
  ) => boolean;
  shouldShowPremiumIndicator: (slides: Slide[]) => boolean;
  /** Open the sidebar "home" view directly (e.g. from SchoolPlanTrialsCounter). */
  openSchoolPlanTrialInfo: (source: string) => void;
  isPremiumTestPrepPaywallVisible: boolean;
  isFeatureEnabled: boolean;
  isNotEnterprise: boolean;
  premiumTestPrepRemaining: number;
  premiumTestPrepHasReachedLimit: boolean;
}

const PremiumTestPrepPlaywallContext = createContext<
  PremiumTestPrepPlaywallContextType | undefined
>(undefined);

/**
 * Decide which SchoolPlanTrialModal state to show, or `null` when the user
 * should be allowed through without a modal.
 *
 *  - used === 0                                      → first-run
 *  - US & backend limit === 4 & remaining === 1     → email-gate (forgiveness)
 *  - remaining === 0                                → hard-block
 *  - anything else (middle of the trial)            → null (no modal)
 */
function pickModalState(
  used: number,
  remaining: number,
  limit: number,
  country: string | undefined,
): SchoolPlanTrialState | null {
  if (used === 0) return 'first-run';
  if (!isInternational(country) && limit >= 4 && remaining === 1) return 'email-gate';
  if (remaining <= 0) return 'hard-block';
  return null;
}

export const PremiumTestPrepPlaywallProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const isFeatureEnabled = useFeatureFlag('premium-testprep-play-limit') ?? false;
  const { accessBadge: userAccessBadge, isFetched } = useAccessBadge();
  const { creditState } = useTeachingCreditState();
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const trackAttioEvent = useAttioEventTracking();
  const trackPaywallEmail = usePaywallEmailTracking();

  const [isPaywallVisible, setIsPaywallVisible] = useState(false);
  const [modalState, setModalState] = useState<SchoolPlanTrialState>('first-run');
  // Source captured at prompt time so it can be threaded into the modal and
  // attached to every CTA-click event fired from inside the modal.
  const [paywallSource, setPaywallSource] = useState<string | undefined>(undefined);
  const [pendingOnConfirm, setPendingOnConfirm] = useState<(() => void) | null>(null);

  const isNotEnterprise = useMemo(
    () =>
      isFetched &&
      userAccessBadge?.license?.coarseLevel !== LicenseCoarseLevels.enterprise,
    [isFetched, userAccessBadge],
  );

  const premiumTestPrepPlayed = creditState?.premiumTestPrepPlayed ?? 0;
  const premiumTestPrepLimit = creditState?.premiumTestPrepLimit ?? FALLBACK_LIMIT;
  const premiumTestPrepRemaining = Math.max(
    0,
    premiumTestPrepLimit - premiumTestPrepPlayed,
  );
  const premiumTestPrepHasReachedLimit = premiumTestPrepRemaining <= 0;

  const country = userAccessBadge?.organization?.country;
  const region = userAccessBadge?.organization?.region;

  const shouldShowPremiumIndicator = useCallback(
    (slides: Slide[]) => {
      if (!isFeatureEnabled || !isFetched) return false;
      return isPremiumTestPrepPresentation(slides);
    },
    [isFeatureEnabled, isFetched],
  );

  const eventBaseProps = useMemo(
    () => ({
      premiumTestPrepPlayed,
      premiumTestPrepRemaining,
      premiumTestPrepLimit,
      hasReachedLimit: premiumTestPrepHasReachedLimit,
      plan: userAccessBadge?.license?.coarseLevel,
      subPlan: userAccessBadge?.license?.fineLevel,
      organizationId: userAccessBadge?.organization?.id,
    }),
    [
      premiumTestPrepPlayed,
      premiumTestPrepRemaining,
      premiumTestPrepLimit,
      premiumTestPrepHasReachedLimit,
      userAccessBadge,
    ],
  );

  const checkAndPromptPremiumTestPrepPaywall: PremiumTestPrepPlaywallContextType['checkAndPromptPremiumTestPrepPaywall'] = useCallback(
    (slides, source, onConfirm, options) => {
      if (!isFeatureEnabled || !isNotEnterprise) return false;
      if (!isPremiumTestPrepPresentation(slides)) return false;

      const nextState = pickModalState(
        premiumTestPrepPlayed,
        premiumTestPrepRemaining,
        premiumTestPrepLimit,
        country,
      );
      if (!nextState) return false;
      if (nextState === 'first-run' && options?.skipFirstRun) return false;

      metrics.logEvent('PremiumTestPrep.Prompted', {
        source,
        state: nextState,
        ...eventBaseProps,
      });

      if (nextState === 'hard-block') {
        trackEvent('premium_testprep_start_blocked', { source, ...eventBaseProps });
        trackAttioEvent('premium_testprep_blocked', {
          organizationId: userAccessBadge?.organization?.id,
          userRole: userAccessBadge?.user?.profileInfo?.role,
          email: userAccessBadge?.user?.email,
          firstName: userAccessBadge?.user?.firstName,
          lastName: userAccessBadge?.user?.lastName,
          source,
        });
        setPendingOnConfirm(null);
      } else {
        // first-run and email-gate both continue to the lesson start after
        // the user confirms (State 1: Start Now button, State 2: email submit).
        setPendingOnConfirm(() => onConfirm);
      }

      setModalState(nextState);
      setPaywallSource(source);
      setIsPaywallVisible(true);
      return true;
    },
    [
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepPlayed,
      premiumTestPrepRemaining,
      premiumTestPrepLimit,
      country,
      metrics,
      eventBaseProps,
      trackEvent,
      trackAttioEvent,
      userAccessBadge,
    ],
  );

  const openSchoolPlanTrialInfo = useCallback(
    (source: string) => {
      metrics.logEvent('PremiumTestPrep.InfoOpened', {
        source,
        ...eventBaseProps,
      });
      setModalState('home');
      setPaywallSource(source);
      setPendingOnConfirm(null);
      setIsPaywallVisible(true);
    },
    [metrics, eventBaseProps],
  );

  const handleClose = useCallback(() => {
    metrics.logEvent('PremiumTestPrep.PaywallDismissed', {
      state: modalState,
      ...eventBaseProps,
    });
    setIsPaywallVisible(false);
    setPendingOnConfirm(null);
  }, [metrics, modalState, eventBaseProps]);

  const handleStartLesson = useCallback(
    (capturedEmail: string | undefined) => {
      metrics.logEvent('PremiumTestPrep.LessonStarted', {
        state: modalState,
        capturedEmail: !!capturedEmail,
        ...eventBaseProps,
      });
      if (capturedEmail) {
        trackPaywallEmail(capturedEmail, {
          paywallType: 'school-plan-trial',
          source: paywallSource,
          modalState,
        });
        trackAttioEvent('premium_testprep_email_submitted', {
          organizationId: userAccessBadge?.organization?.id,
          userRole: userAccessBadge?.user?.profileInfo?.role,
          email: capturedEmail,
          firstName: userAccessBadge?.user?.firstName,
          lastName: userAccessBadge?.user?.lastName,
          source: paywallSource,
        });
      }
      pendingOnConfirm?.();
      setPendingOnConfirm(null);
      // Leave modal open briefly so the "Starting your lesson…" view can render;
      // it closes itself when dismissed.
    },
    [
      metrics,
      modalState,
      eventBaseProps,
      pendingOnConfirm,
      trackPaywallEmail,
      trackAttioEvent,
      userAccessBadge,
      paywallSource,
    ],
  );

  const handleEmailSubmitHardBlock = useCallback(
    (email: string) => {
      trackPaywallEmail(email, {
        paywallType: 'school-plan-trial',
        source: paywallSource,
        modalState,
      });
      trackAttioEvent('premium_testprep_email_submitted', {
        organizationId: userAccessBadge?.organization?.id,
        userRole: userAccessBadge?.user?.profileInfo?.role,
        email,
        firstName: userAccessBadge?.user?.firstName,
        lastName: userAccessBadge?.user?.lastName,
        source: paywallSource,
      });
    },
    [modalState, trackPaywallEmail, trackAttioEvent, userAccessBadge, paywallSource],
  );

  const contextValue = useMemo<PremiumTestPrepPlaywallContextType>(
    () => ({
      checkAndPromptPremiumTestPrepPaywall,
      shouldShowPremiumIndicator,
      openSchoolPlanTrialInfo,
      isPremiumTestPrepPaywallVisible: isPaywallVisible,
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepRemaining,
      premiumTestPrepHasReachedLimit,
    }),
    [
      checkAndPromptPremiumTestPrepPaywall,
      shouldShowPremiumIndicator,
      openSchoolPlanTrialInfo,
      isPaywallVisible,
      isFeatureEnabled,
      isNotEnterprise,
      premiumTestPrepRemaining,
      premiumTestPrepHasReachedLimit,
    ],
  );

  return (
    <PremiumTestPrepPlaywallContext.Provider value={contextValue}>
      {children}
      <SchoolPlanTrialModal
        visible={isPaywallVisible}
        onClose={handleClose}
        state={modalState}
        country={country}
        region={region}
        // Display only — derived from `played` so the badge debits as soon
        // as the user uses a trial (the hidden 4th forgiveness slot keeps raw
        // `remaining` lingering at the cap otherwise). Modal-state-picking
        // and analytics still read raw `premiumTestPrepRemaining` above.
        remaining={computeVisibleTrialsLeft(premiumTestPrepPlayed)}
        source={paywallSource}
        onStartLesson={handleStartLesson}
        onEmailSubmit={handleEmailSubmitHardBlock}
      />
    </PremiumTestPrepPlaywallContext.Provider>
  );
};

const defaultContext: PremiumTestPrepPlaywallContextType = {
  checkAndPromptPremiumTestPrepPaywall: () => false,
  shouldShowPremiumIndicator: () => false,
  openSchoolPlanTrialInfo: () => undefined,
  isPremiumTestPrepPaywallVisible: false,
  isFeatureEnabled: false,
  isNotEnterprise: false,
  premiumTestPrepRemaining: 0,
  premiumTestPrepHasReachedLimit: false,
};

export const usePremiumTestPrepPlaywall = (): PremiumTestPrepPlaywallContextType => {
  const context = useContext(PremiumTestPrepPlaywallContext);
  return context ?? defaultContext;
};

// Re-export for any caller that wants the shared "3 trials" display constant.
export { VISIBLE_TRIAL_LIMIT } from '../../hooks/useSchoolPlanContentAccess';
